import { IfAbility } from '@efdigital/ll-addon-auth';
import type { File, Folder } from '@efdigital/ll-addon-data';
import { DataTypeContent } from '@efdigital/ll-addon-data/data-types';
import { findFolderAndContents } from '@efdigital/ll-admin-v2/data/builders/content/files';
import { parseTypedFileOrFolderId } from '@efdigital/ll-admin-v2/lib/file-icon';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { Store } from '@warp-drive/core';
import { Request } from '@warp-drive/ember';
import BsModal from 'ember-bootstrap/components/bs-modal';

import { EditContext, EditingStoppedReason } from '../edit-context.gts';
import FileUploader from '../files/file-uploader.gts';
import FolderBreadcrumb, {
  FolderBreadcrumbItem,
} from '../files/folder-breadcrumb.gts';
import { FolderContents } from '../files/folder-contents.gts';
import FolderCreate from '../files/folder-create.gts';
import { FileItem, FolderItem } from '../files/folder-items.gts';

export type FilePickerResult = {
  item: File | Folder;
  type: DataTypeContent.File | DataTypeContent.Folder;
};

type Signature = {
  Args: {
    onSelect?: (result: FilePickerResult) => void;
    canSelectFolders?: boolean;
    canSelectFiles?: boolean;
    navigationFolders: { id: string; label: string; help: string }[];
  };
  Blocks: {
    input: [showModal: (startingPath: string) => void];
  };
};

export class BrowseFolderState {
  @tracked folderId: string = '|';
}

export default class FilePicker extends Component<Signature> {
  @tracked isOpen = false;
  @tracked isFileModalOpen = false;
  @tracked isFolderModalOpen = false;
  @tracked newFolder: Folder | null = null;
  @tracked state = new BrowseFolderState();
  @service declare store: Store;

  navigateToFolder = (item: { id: string | null }) => {
    this.state.folderId = item.id!;
  };

  isWithinFolder = (parentFolderId: string) => {
    return this.state.folderId.startsWith(parentFolderId);
  };

  get globalFolderPath() {
    return '|global';
  }

  showModal = (startingPath: string) => {
    const { type, id } = parseTypedFileOrFolderId(startingPath);
    const initialPath =
      type === DataTypeContent.Folder
        ? id
        : id.substring(0, id.lastIndexOf('|'));

    this.state.folderId = initialPath;
    this.isOpen = true;
  };

  modalClosed = () => {
    this.isOpen = false;
    this.isFileModalOpen = false;
    this.isFolderModalOpen = false;
  };

  get findFolder() {
    return findFolderAndContents(this.state.folderId);
  }

  selectFile = (file: File) => {
    if (!this.args.canSelectFiles) {
      return;
    }

    this.args.onSelect?.({ item: file, type: DataTypeContent.File });
    this.isOpen = false;
  };

  selectFolder = (folder: Folder) => {
    if (!this.args.canSelectFolders) {
      return;
    }

    this.args.onSelect?.({ item: folder, type: DataTypeContent.Folder });
    this.isOpen = false;
  };

  get startBreadcrumbFrom() {
    if (this.args.navigationFolders?.length) {
      for (const folder of this.args.navigationFolders) {
        if (this.isWithinFolder(folder.id)) {
          return folder.id;
        }
      }
    }

    return '|';
  }

  showFileModal = () => {
    this.isFileModalOpen = true;
  };

  hideFileModal = () => {
    this.isFileModalOpen = false;
  };

  showFolderModal = () => {
    this.newFolder = this.store.createRecord<Folder>(DataTypeContent.Folder, {
      name: '',
      parent: this.state.folderId,
    });
    this.isFolderModalOpen = true;
  };

  hideFolderModal = (reason: EditingStoppedReason) => {
    if (reason === EditingStoppedReason.Cancelled) {
      this.isFolderModalOpen = false;
      this.store.unloadRecord(this.newFolder);
      this.newFolder = null;
    } else {
      const newFolderId = this.newFolder?.id;
      this.isFolderModalOpen = false;
      this.newFolder = null;
      this.navigateToFolder({ id: newFolderId! });
    }
  };

  <template>
    {{yield this.showModal to="input"}}
    <BsModal
      @open={{this.isOpen}}
      @onHidden={{this.modalClosed}}
      @size="xl"
      @renderInPlace={{true}}
      @backdropClose={{false}}
      as |modal|
    >
      <modal.header>
        {{#if @navigationFolders.length}}
          <div class="nav nav-pills">
            {{#each @navigationFolders as |folder|}}
              <div class="nav-item">
                <button
                  type="button"
                  title={{folder.help}}
                  class="nav-link fw-bold
                    {{if (this.isWithinFolder folder.id) 'active'}}"
                  {{on "click" (fn this.navigateToFolder folder)}}
                >{{folder.label}}</button>
              </div>
            {{/each}}
          </div>
        {{/if}}
      </modal.header>
      <modal.body>
        <Request @query={{this.findFolder}}>
          <:content as |response|>
            <div class="d-flex justify-content-between align-items-baseline">
              <FolderBreadcrumb
                @folderAndContents={{response.data}}
                @startFrom={{this.startBreadcrumbFrom}}
              >
                <:breadcrumb as |crumb|>
                  <FolderBreadcrumbItem
                    @crumb={{crumb}}
                    @onClick={{this.navigateToFolder}}
                  />
                </:breadcrumb>
              </FolderBreadcrumb>
              <div class="d-inline-flex gap-1">
                <IfAbility @ability="library:files" @atLeast="editor">
                  <:if>
                    <button
                      class="btn btn-sm btn-outline-secondary text-nowrap"
                      type="button"
                      {{on "click" this.showFileModal}}
                    ><iconify-icon inline icon="ph:file-bold" />
                      Upload File</button>

                    <BsModal
                      @open={{this.isFileModalOpen}}
                      @onHidden={{this.hideFileModal}}
                      @size="xl"
                      @backdropClose={{false}}
                      as |modal|
                    >
                      <modal.header>
                        <h2 class="modal-title fs-5">Add File</h2>
                      </modal.header>
                      <modal.body>
                        <FileUploader
                          @parentFolderId={{this.state.folderId}}
                          @onFileUploaded={{this.selectFile}}
                        />
                      </modal.body>
                    </BsModal>
                  </:if>
                </IfAbility>
                <IfAbility @ability="library:files" @atLeast="editor">
                  <:if>
                    <button
                      class="btn btn-sm btn-outline-secondary text-nowrap"
                      type="button"
                      {{on "click" this.showFolderModal}}
                    >
                      <iconify-icon inline icon="ph:folder-bold" />
                      New Folder</button>

                    <BsModal
                      @open={{this.isFolderModalOpen}}
                      @size="lg"
                      @backdropClose={{false}}
                      as |modal|
                    >
                      <modal.header>
                        <h2 class="modal-title fs-5">New Folder</h2>
                      </modal.header>
                      <modal.body>
                        {{#if this.newFolder}}
                          <EditContext
                            @onEditingStopped={{this.hideFolderModal}}
                            as |context|
                          >
                            <FolderCreate
                              @item={{this.newFolder}}
                              @context={{context}}
                            />
                          </EditContext>
                        {{/if}}
                      </modal.body>
                    </BsModal>
                  </:if>
                </IfAbility>
                {{#if @canSelectFolders}}
                  <button
                    class="btn btn-sm btn-primary text-nowrap btn-magical-border"
                    type="button"
                    {{on "click" (fn this.selectFolder response.data)}}
                  >Use Entire Folder</button>
                {{/if}}
              </div>
            </div>

            <FolderContents
              @folderAndContents={{response.data}}
              @containerClass="row row-cols-1 row-cols-md-3 row-cols-lg-4 g-3"
            >
              <:folder as |folder|>
                <FolderItem
                  @item={{folder}}
                  @onClick={{this.navigateToFolder}}
                  class="h-100 text-primary border-primary-subtle bg-body-tertiary"
                />
              </:folder>
              <:file as |file|>
                <FileItem
                  @item={{file}}
                  @onClick={{this.selectFile}}
                  class="h-100 text-secondary border-secondary btn-magical-border"
                  title="Select file"
                />
              </:file>
            </FolderContents>
          </:content>
        </Request>
      </modal.body>
    </BsModal>
  </template>
}
